import { Container } from 'aurelia-dependency-injection';
import { EventAggregator } from 'aurelia-event-aggregator';
import { I18N } from 'aurelia-i18n';
import { NavigationInstruction, RouteConfig, Router } from 'aurelia-router';
import { BindingSignaler } from 'aurelia-templating-resources';

import { BaseViewModel } from '~/base-view-model';
import {
	GlobalEvents,
	ShortcutKey,
	TREE_VIEW_SESSION_VARIABLE_NAME,
} from '~/constants';
import { CustomisationEditorSelectDialogModel } from '~/forms/customisation-editor/customisation-editor-select-dialog';
import { CustomisationEditorSelectDialogManager } from '~/forms/customisation-editor/customisation-editor-select-dialog-manager';
import { LayoutOptions } from '~/layouts/layout-options';
import I18nConfigLabelService from '~/utils/i18n-config-label-service';
import { KeyboardShortcut } from '~/utils/keyboard-shortcut';
import { Logger } from '~/utils/logger';
import { LockUntilReady } from '~/utils/utils';

/**
 * We handle the i18n translation in this form. If any string inside of locale .json files
 * doesn't have a key ending with --exact suffix, some keywords like ('member', 'fund', 'client')
 * will be replaced by the corresponding alias from Configuration table. See logics in
 * i18n-config-label-service.ts
 * If the translation string doesn't want to be replaced by the alias, its key should end with '--exact'
 * suffix.
 */
export default class I18nForm extends BaseViewModel {
	private activatedRouteConfig: RouteConfig;
	private lockUntilLayoutReadyToAcceptSetOptions = new LockUntilReady();
	private localeLoadedLock = new LockUntilReady();
	protected functionName: string = '_';

	constructor(
		protected readonly router: Router,
		protected readonly i18n: I18N,
		protected readonly bindingSignaler: BindingSignaler,
		protected readonly eventAggregator: EventAggregator,
		protected readonly element: Element,
		private readonly namespaces?: string[],
	) {
		super();
	}

	protected activate(
		params: any,
		routeConfig: RouteConfig,
		navigationInstruction: NavigationInstruction,
	): Promise<any> {
		this.activatedRouteConfig = routeConfig;
		let namespaces = this.namespaces;
		if (
			(!namespaces || namespaces.length === 0) &&
			navigationInstruction.config.name
		) {
			namespaces = [navigationInstruction.config.name];
		}

		return this.loadLanguageResources(namespaces).catch((error) => {
			Logger.error('Error on activate', error);
			this.router.navigateToRoute('error');
		});
	}

	private replaceConfigurableStrings(nameSpace: string): Promise<boolean> {
		const labelService = Container.instance.get(I18nConfigLabelService);
		return labelService.replaceI18nStringsWithConfigLabel(
			this.i18n,
			nameSpace,
		);
	}

	protected bindCustomisationEditorShortcutKey(
		customisationEditorSelectDialogManager: CustomisationEditorSelectDialogManager,
	): void {
		if (sessionStorage.getItem(TREE_VIEW_SESSION_VARIABLE_NAME) !== '1') {
			// Customisation editor is not currently supported in tree-view
			KeyboardShortcut.subscribe(
				this,
				ShortcutKey.CustomisationEditor,
				() =>
					this.checkAuthAndOpenCustomisationEditorSelectDialog(
						customisationEditorSelectDialogManager,
					),
			);
		}
	}

	protected detached(): void {
		if (KeyboardShortcut.isSubscribed(this)) {
			KeyboardShortcut.unsubscribeAll(this);
		}
	}

	protected checkAuthAndOpenCustomisationEditorSelectDialog(
		customisationEditorSelectDialogManager: CustomisationEditorSelectDialogManager,
	) {
		const model: CustomisationEditorSelectDialogModel = {
			defaultFunctionName: this.functionName,
		};
		customisationEditorSelectDialogManager.checkAuthAndOpenCustomisationEditorSelectDialog(
			this.router,
			model,
		);
	}

	private loadLanguageResources(
		namespaces?: string[] | string,
	): Promise<any> {
		if (namespaces && namespaces.length > 0) {
			return new Promise((resolve, reject) => {
				this.i18n.i18next.loadNamespaces(
					namespaces,
					(errorMessages: string[]) => {
						if (!errorMessages || errorMessages.length === 0) {
							this.i18n.updateTranslations(this.element);
							const configLabelPromises: Array<Promise<boolean>> =
								[];
							if (Array.isArray(namespaces)) {
								namespaces.forEach((ns) =>
									configLabelPromises.push(
										this.replaceConfigurableStrings(ns),
									),
								);
							} else {
								configLabelPromises.push(
									this.replaceConfigurableStrings(namespaces),
								);
							}

							this.bindingSignaler.signal(
								'aurelia-translation-signal',
							);
							this.localeLoadedLock.triggerOnce();
							resolve(
								Promise.all<boolean>(configLabelPromises).catch(
									Logger.error,
								),
							);
						} else {
							Logger.error(
								'error(s) loading language resources:\n' +
									errorMessages.join('\n'),
							);
							// NOTE: Jake: 2019-02-28
							// Not sure if we should crash if translations don't load or not.
							// Since some are missing for now, we opt to not crash.
							resolve([]);
						}
					},
				);
			});
		} else {
			this.localeLoadedLock.triggerOnce();
			return Promise.resolve();
		}
	}

	get hasErrorMessage(): boolean {
		return this.router && this.router.currentInstruction
			? this.router.currentInstruction.config.settings.errorMessage
			: false;
	}

	get errorMessage(): string | null {
		return this.hasErrorMessage
			? String(
					this.router.currentInstruction.config.settings.errorMessage,
			  )
			: null;
	}

	/**
	 *  This setter must be called after i18n-form#activate is called, as
	 *  this.activatedRouteConfig will only be available at activate() hook.
	 */
	set errorMessage(error: string) {
		if (DEVELOPMENT && !this.activatedRouteConfig) {
			throw Error(
				"activatedRouteConfig isn't available. Did you called it before activate() hook in i18n-form?",
			);
		}
		if (this.activatedRouteConfig) {
			this.activatedRouteConfig.settings.errorMessage = error;
		}
	}
}
